import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Building2, MapPin, Phone, Mail, Calendar, Edit2, Package as PackageIcon, CreditCard, Clock } from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function ClinicDetails() {
  const [form, setForm] = useState({
    name: '',
    address: '',
    contact_number: '',
    email: '',
    established_year: '',
  });
  const [packageInfo, setPackageInfo] = useState(null);
  const [isEditing, setIsEditing] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const clinicId = localStorage.getItem('clinicId');
    
    // Fetch Clinic details
    axios.get(`${BASE_URL}/api/clinics/details/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    })
    .then(res => {
      console.log('Clinic details response:', res.data);
      setForm(res.data);
      // Check if any data exists (not just name)
      if (res.data.name && res.data.name.trim() !== '') {
        setIsEditing(false);
      }
    })
    .catch(err => {
      console.log('Error fetching clinic details:', err.response?.data || err.message);
    });

    // Fetch package information
    if (clinicId) {
      axios.get(`${BASE_URL}/api/superadmin/${clinicId}/package/current/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      .then(res => {
        setPackageInfo(res.data);
      })
      .catch(err => console.log('Error fetching package:', err.response?.data || err.message));
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('accessToken');
    axios.put(`${BASE_URL}/api/clinics/details/`, form, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    })
    .then(() => {
      notify.success('Details updated successfully');
      setIsEditing(false);
    })
    .catch(err => {
      console.error('Save error:', err.response?.data || err.message);
      notify.error('Error saving details');
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-700/20 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Clinic Profile</h2>
            <p className="text-medical-100 text-sm">Manage your Clinic information</p>
          </div>
        </div>
      </motion.div>

      {/* Package Information Card */}
      {packageInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <PackageIcon className="w-5 h-5 text-primary-400" />
              Current Package
            </h3>
            <span className="px-4 py-2 bg-primary-500/20 text-primary-300 rounded-lg font-semibold">
              {packageInfo.package_name}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <CreditCard className="w-4 h-4" />
                <span>Credit Limit</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{packageInfo.credit_limit || 0}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <Clock className="w-4 h-4" />
                <span>Days Limit</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{packageInfo.days_limit || 0}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-1">
                <PackageIcon className="w-4 h-4" />
                <span>Package Status</span>
              </div>
              <p className="text-lg font-semibold text-green-600">Active</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Clinic Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border border-slate-200 p-5"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-slate-900">Clinic Information</h3>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit Details
            </button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-2" />
                Clinic Name
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter Clinic name"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Address
              </label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Enter address"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Contact Number
                </label>
                <input
                  name="contact_number"
                  value={form.contact_number}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Established Year
              </label>
              <input
                name="established_year"
                type="number"
                value={form.established_year}
                onChange={handleChange}
                placeholder="Enter year"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 mt-6">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-5 py-2 border border-slate-300 text-slate-700 font-medium rounded-md hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-primary-500/100 text-white font-medium rounded-md hover:bg-medical-600 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                  <Building2 className="w-4 h-4" />
                  <span>Clinic Name</span>
                </div>
                <p className="text-lg font-semibold text-slate-900">{form.name}</p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                  <Calendar className="w-4 h-4" />
                  <span>Established Year</span>
                </div>
                <p className="text-lg font-semibold text-slate-900">{form.established_year}</p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                  <Phone className="w-4 h-4" />
                  <span>Contact Number</span>
                </div>
                <p className="text-lg font-semibold text-slate-900">{form.contact_number}</p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </div>
                <p className="text-lg font-semibold text-slate-900">{form.email}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                <MapPin className="w-4 h-4" />
                <span>Address</span>
              </div>
              <p className="text-lg font-semibold text-slate-900">{form.address}</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}


